import React, { useEffect, useState, useRef } from 'react';
import { Field } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createSlug } from '../../../util/urlHelpers';
import { useConfiguration } from '../../../context/configurationContext.js';

import { FieldSelect } from '../../../components';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { formatMoney } from '../../../util/currency';

import css from './PriceVariantPicker.module.css';

const { Money } = sdkTypes;

const DEFAULT_PRICE_VARIANT_NAME = 'default-variant-name';

// Calculate space width for particular font family and size
function getSpaceWidth(font) {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    context.font = font;

    return context.measureText(' ').width;
  }
}

// Calculate option words width for particular font family and size
const calculateWordsWidth = (firstWord, secondWord) => {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = '14px Inter-Regular';
    return context.measureText(firstWord).width + context.measureText(secondWord).width;
  }
};

// Calculate space count to insert between package name and price
const calculateSpaces = (selectWidthNoPad, firstWord, secondWord) => {
  const spaceWidth = getSpaceWidth('14px Inter-Regular');
  const wordsWidth = calculateWordsWidth(firstWord, secondWord).toFixed(2);
  const width = selectWidthNoPad - wordsWidth;

  const spacesToAdd = Math.floor(width / spaceWidth) - 10; // too many without '- 10'

  return spacesToAdd > 0 ? '\u00A0'.repeat(spacesToAdd) : '\u00A0';
};

const VariantNameMaybe = props => {
  const { className, priceVariant } = props;
  return priceVariant?.name ? (
    <div className={className}>
      <FormattedMessage
        id="PriceVariantPicker.onePriceVariantOnly"
        values={{ priceVariantName: priceVariant?.name }}
      />
    </div>
  ) : null;
};

const FieldHidden = props => {
  const { name, ...rest } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.hidden} {...rest}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

const PriceVariantPicker = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const { priceVariants, onPriceVariantChange, disabled } = props;
  const hasMultiplePriceVariants = priceVariants?.length > 1;
  const hasOnePriceVariant = priceVariants?.length === 1;

  const [selectWidthNoPad, setSelectWidthNoPad] = useState(150); // Default width assumption
  const selectRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // To get select inner width
      if (selectRef.current) {
        const selectWidth = selectRef.current.clientWidth;

        const style = window.getComputedStyle(selectRef.current);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;

        const contentWidth = selectWidth - paddingLeft - paddingRight;

        setSelectWidthNoPad(contentWidth);
      }
    }
  }, []);

  return hasMultiplePriceVariants ? (
    <FieldSelect
      name="priceVariantName"
      id="priceVariant"
      className={css.priceVariantFieldSelect}
      selectClassName={css.priceVariantSelect}
      label={intl.formatMessage({ id: 'PriceVariantPicker.priceVariantLabel' })}
      onChange={onPriceVariantChange}
      disabled={disabled}
      showLabelAsDisabled={disabled}
      selectRef={selectRef}
    >
      <option disabled value="" key="unselected">
        {intl.formatMessage({ id: 'PriceVariantPicker.priceVariantUnselected' })}
      </option>

      {typeof window !== 'undefined' && priceVariants.map(pv => {

        const fee = formatMoney(intl, new Money(pv.priceInSubunits, config.currency));
        const spaces = calculateSpaces(selectWidthNoPad, pv.name, fee);
        return (
          <option value={pv.name} key={pv.name} data-slug={createSlug(pv.name)}>
            {pv.name}{spaces}{fee}
          </option>
        );
      })}


    </FieldSelect>
  ) : hasOnePriceVariant ? (
    <>
      <VariantNameMaybe priceVariant={priceVariants?.[0]} className={css.priceVariantName} />
      <FieldHidden
        name="priceVariantName"
        format={value => {
          return value == null ? DEFAULT_PRICE_VARIANT_NAME : value;
        }}
        parse={value => {
          const response = value === DEFAULT_PRICE_VARIANT_NAME ? null : value;
          return response;
        }}
      />
    </>
  ) : null;
};

export default PriceVariantPicker;
